import * as XLSX from 'xlsx';
import { HoursReport, ImportMarkRow } from '../../shared/services/turnoGeo/types';

const r2 = (n: number) => Math.round(n * 100) / 100;
const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

/** Excel del reporte: hojas Resumen, Semanas y (si viene el detalle) Días. */
export function exportHoursReport(report: HoursReport, includePay: boolean) {
  const resumen = report.employees.map((e) => ({
    Empleado: e.fullName, Documento: e.documentNumber ?? '',
    'Horas netas': r2(e.netHours), 'Semanas bajo mínimo': e.weeksBelowMin, 'Turnos abiertos': e.openShifts,
    'Extra diurna (h)': r2(e.extra.diurnaOrdinaria), 'Extra nocturna (h)': r2(e.extra.nocturnaOrdinaria),
    'Extra festiva diurna (h)': r2(e.extra.diurnaFestiva), 'Extra festiva nocturna (h)': r2(e.extra.nocturnaFestiva),
    'Recargo nocturno 35% (h)': r2(e.nightSurchargeHours), 'Horas festivas (h)': r2(e.festiveHours.total),
    ...(includePay && e.pay ? { 'Extras ($)': Math.round(e.pay.extras), 'Recargo nocturno ($)': Math.round(e.pay.recargoNocturno), 'Festivos ($)': Math.round(e.pay.festivos), 'Total ($)': Math.round(e.pay.total) } : {}),
  }));
  const semanas = report.employees.flatMap((e) => e.weeks.map((w) => ({
    Empleado: e.fullName, 'Semana desde': w.weekStart, 'Semana hasta': w.weekEnd, 'Días con marca': w.days,
    'Horas netas': r2(w.netHours), 'Mínimo': w.minHours, 'Faltan (h)': r2(w.deficitHours), 'Excedente (h)': r2(w.excessHours),
    Cumple: w.partial ? 'Semana parcial' : w.meetsMin ? 'Sí' : 'No',
  })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumen), 'Resumen');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(semanas), 'Semanas');
  const dias = report.employees.flatMap((e) => (e.days ?? []).map((d) => ({
    Empleado: e.fullName, Fecha: d.date, Día: DOW[d.dayOfWeek], Festivo: d.holidayName ?? (d.isFestive ? 'Domingo' : ''),
    Entrada: d.entry, Salida: d.exit ?? 'ABIERTO', 'Almuerzo (min)': d.lunchMinutes, 'Almuerzo auto': d.lunchAuto ? 'Sí' : '',
    'Horas netas': r2(d.netHours), 'Extra diurna': r2(d.extra.diurnaOrdinaria), 'Extra nocturna': r2(d.extra.nocturnaOrdinaria),
    'Extra fest. diurna': r2(d.extra.diurnaFestiva), 'Extra fest. nocturna': r2(d.extra.nocturnaFestiva), 'Recargo nocturno (h)': r2(d.nightSurchargeHours),
  })));
  if (dias.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dias), 'Días');
  XLSX.writeFile(wb, `TurnoGeo_Horas_${report.from}_a_${report.to}.xlsx`);
}

const norm = (h: unknown) => String(h ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
const MAP: Record<string, keyof ImportMarkRow> = {
  EMAIL: 'email', CORREO: 'email', CC: 'document', CEDULA: 'document', DOCUMENTO: 'document', FECHA: 'date',
  ENTRADA: 'entry', 'SALIDA ALMUERZO': 'lunchOut', 'ENTRADA ALMUERZO': 'lunchIn', SALIDA: 'exit',
};

function cellDate(v: unknown): string {
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'number') { const d = XLSX.SSF.parse_date_code(v); return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`; }
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : s;
}
function cellTime(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number') { const mins = Math.round((v % 1) * 1440); return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`; }
  const m = String(v).trim().match(/^(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(v).trim();
}

/** Lee el Excel de marcaciones históricas (plantilla: EMAIL o CC, FECHA, ENTRADA, SALIDA ALMUERZO, ENTRADA ALMUERZO, SALIDA). */
export async function parseMarksFile(file: File): Promise<ImportMarkRow[]> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('El archivo no tiene hojas.');
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: null, blankrows: false });
  if (matrix.length < 2) throw new Error('El archivo no trae filas (solo encabezado o vacío).');
  const fields = (matrix[0] as unknown[]).map((h) => MAP[norm(h)] ?? null);
  if (!fields.includes('date') || !fields.includes('entry') || !(fields.includes('email') || fields.includes('document'))) {
    throw new Error('Faltan columnas: se necesita EMAIL (o CC), FECHA y ENTRADA. Descarga la plantilla.');
  }
  return matrix.slice(1).map((line) => {
    const o: Record<string, string> = {};
    (line as unknown[]).forEach((v, i) => {
      const f = fields[i];
      if (!f) return;
      o[f] = f === 'date' ? cellDate(v) : ['entry', 'lunchOut', 'lunchIn', 'exit'].includes(f) ? cellTime(v) : String(v ?? '').trim();
    });
    return o as unknown as ImportMarkRow;
  }).filter((r) => r.date || r.entry);
}

export function downloadMarksTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    ['EMAIL', 'CC', 'FECHA', 'ENTRADA', 'SALIDA ALMUERZO', 'ENTRADA ALMUERZO', 'SALIDA'],
    ['persona@empresa.com', '', '2025-03-03', '08:00', '12:00', '13:00', '17:24'],
    ['', '1234567890', '2025-03-04', '08:02', '', '', '17:30'],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Marcaciones');
  XLSX.writeFile(wb, 'Plantilla_marcaciones_TurnoGeo.xlsx');
}
